import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { atletePhotosFunc } from "../../../../data-store/actions/atlete-actions";
import {
  athleteJuezRegistries,
  createJuezRegistry,
} from "../../../../data-store/actions/events-actions";
import { tiempoASegundos, segundosATiempo } from "../../../../services/utils";
import usePuntosControl from "../../../../services/usePuntosControl";

import AtleteControls from "./AtleteControls";
import AtleteCharts from "./AtleteCharts";
import AtletePhotos from "./AtletePhotos";
import { puntoFotograficoPrevRuta } from "../../../../firebase/firebasejs";
import SimpleDialogDemo from "../../Modals/SimpleDialog";
import {
  Typography,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
} from "@material-ui/core";

// Busca la hora de salida configurada para la serie/modalidad del atleta
// (modalidades/{codMod}/hSalidas/serie_{serie}/hS, mismo dato que ya usa
// TimingUtils.jsx para calcular resultados) — null si no está configurada
// (ej. modalidad contrarreloj, o esa serie no tiene hora definida todavía).
export function obtenerHoraSalidaSerie(eventSubEvents, resultado) {
  if (!eventSubEvents || !resultado) return null;
  const modalidad = eventSubEvents["modalidad_" + resultado.codMod];
  const hSalida =
    modalidad && modalidad.hSalidas && modalidad.hSalidas["serie_" + resultado.serie];
  if (!hSalida || !hSalida.hS) return null;
  const fecha = new Date(hSalida.hS.replace(" ", "T"));
  return isNaN(fecha.getTime()) ? null : fecha;
}

// Separa un Date en los mismos dos strings que ya usan los campos
// "fecha"/"hora" de este formulario (YYYY-MM-DD y HH:MM:SS.mmm).
export function formatearFechaHora(fechaObj) {
  const dd = String(fechaObj.getDate()).padStart(2, "0");
  const mm = String(fechaObj.getMonth() + 1).padStart(2, "0");
  const yyyy = fechaObj.getFullYear();
  const h = String(fechaObj.getHours()).padStart(2, "0");
  const m = String(fechaObj.getMinutes()).padStart(2, "0");
  const s = String(fechaObj.getSeconds()).padStart(2, "0");
  const ms = String(fechaObj.getMilliseconds()).padStart(3, "0");
  return {
    fecha: `${yyyy}-${mm}-${dd}`,
    hora: `${h}:${m}:${s}.${ms}`,
  };
}

// HH:MM:SS, horas de 1 a 3 dígitos (para eventos muy largos) — sin
// milisegundos, a diferencia del campo "hora" (que sí los necesita).
const REGEX_TIEMPO_TRANSCURRIDO = /^\d{1,3}:\d{2}:\d{2}$/;
const REGEX_HORA = /^([01][0-9]|2[0-3]|[0-9]):[0-5][0-9]:[0-5][0-9]\.\d{3}$/;

export default function UpdateRegistryJuez(props) {
  const dispatch = useDispatch();
  const { registry, update, eventDetail, resultado } = props;
  const userID = useSelector((state) => state.atlete.userID);
  const registryJuez = useSelector((state) => state.events.registryJuez);
  const eventUsers = useSelector((state) => state.events.eventUsers);
  const eventSubEvents = useSelector((state) => state.events.eventSubEvents);
  const puntosControl = usePuntosControl();

  const fechaHoraInicial = useMemo(() => {
    const partes = (registry.fechaHora || "").split(" ");
    return { fecha: partes[0] || "", hora: partes[1] || "" };
  }, [registry.fechaHora]);

  const [kPuntoControlValue, setKPuntoControlValue] = useState(
    registry.kPuntoControl
  );
  const [visibleOBorradoValue, setVisibleOBorradoValue] = useState(
    registry.visibleOBorrado !== 0
  );
  // Fecha/hora exactas y tiempo transcurrido desde la salida son dos formas
  // de editar el mismo dato — ambas visibles siempre, sincronizadas en
  // tiempo real: cambiar una recalcula la otra al instante (sin botón).
  const [fechaValue, setFechaValue] = useState(fechaHoraInicial.fecha);
  const [horaValue, setHoraValue] = useState(fechaHoraInicial.hora);
  const [tiempoTranscurrido, setTiempoTranscurrido] = useState("00:00:00");

  const horaSalidaSerie = useMemo(
    () => obtenerHoraSalidaSerie(eventSubEvents, resultado),
    [eventSubEvents, resultado]
  );

  // Arranca (o se actualiza si horaSalidaSerie llega después) el tiempo
  // transcurrido a partir de la fecha/hora actuales — así el campo nunca
  // queda desincronizado del valor real que representa.
  useEffect(() => {
    if (!horaSalidaSerie || !fechaValue || !horaValue) return;
    const fechaRegistro = new Date(`${fechaValue}T${horaValue}`);
    if (isNaN(fechaRegistro.getTime())) return;
    const segundos = Math.round(
      (fechaRegistro.getTime() - horaSalidaSerie.getTime()) / 1000
    );
    setTiempoTranscurrido(segundosATiempo(Math.max(segundos, 0)));
    // Solo se recalcula cuando cambia horaSalidaSerie (al llegar) — los
    // cambios de fechaValue/horaValue ya actualizan tiempoTranscurrido
    // directamente en su propio handler, sin pasar por acá.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horaSalidaSerie]);

  function handleTiempoChange(nuevoValor) {
    setTiempoTranscurrido(nuevoValor);
    if (!horaSalidaSerie || !REGEX_TIEMPO_TRANSCURRIDO.test(nuevoValor)) return;
    const segundos = tiempoASegundos(nuevoValor);
    if (isNaN(segundos)) return;
    const nuevaFecha = new Date(horaSalidaSerie.getTime() + segundos * 1000);
    const formateada = formatearFechaHora(nuevaFecha);
    setFechaValue(formateada.fecha);
    setHoraValue(formateada.hora);
  }

  function recalcularTiempoDesde(fechaStr, horaStr) {
    if (!horaSalidaSerie || !fechaStr || !REGEX_HORA.test(horaStr)) return;
    const fechaRegistro = new Date(`${fechaStr}T${horaStr}`);
    if (isNaN(fechaRegistro.getTime())) return;
    const segundos = Math.round(
      (fechaRegistro.getTime() - horaSalidaSerie.getTime()) / 1000
    );
    setTiempoTranscurrido(segundosATiempo(Math.max(segundos, 0)));
  }

  function handleFechaChange(nuevaFecha) {
    setFechaValue(nuevaFecha);
    recalcularTiempoDesde(nuevaFecha, horaValue);
  }

  function handleHoraChange(nuevaHora) {
    setHoraValue(nuevaHora);
    recalcularTiempoDesde(fechaValue, nuevaHora);
  }

  useEffect(() => {
    //dispatch();
    //athleteJuezRegistries("evento_" + eventDetail.iDEvento, resultado.num)
  }, []);

  function onSubmit() {
    // Antes el botón se deshabilitaba con "!kPuntoControlValue", pero al
    // editar un registro existente ese valor YA viene lleno desde el
    // inicio (no es un cambio real) y el botón quedaba habilitado sin que
    // el usuario tocara nada — mientras que al crear uno nuevo (arranca en
    // 0, el placeholder de "meta") quedaba deshabilitado hasta elegir un
    // punto, dando la impresión de que solo ESE campo "activaba" el botón.
    // Ahora el botón siempre está disponible y se valida al guardar.
    if (!kPuntoControlValue && kPuntoControlValue !== 0) {
      window.alert("Selecciona un punto de control antes de guardar.");
      return;
    }
    if (!REGEX_HORA.test(horaValue)) {
      window.alert("Revisa el formato de la hora (00:00:00.000) antes de guardar.");
      return;
    }

    const object = {
      competidorId: update ? registry.competidorId : userID,
      fechaHora: fechaValue + " " + horaValue,
      iDEvento: eventDetail.iDEvento,
      kPuntoControl: kPuntoControlValue,
      lat: registry.lat,
      lng: registry.lng,
      numero: registry.numero,
      timeStamp: new Date(`${fechaValue}T${horaValue}`).getTime(),
      visibleOBorrado: visibleOBorradoValue ? 1 : 0,
    };

    dispatch(createJuezRegistry(object, update, registry.index));
  }

  function selectList(object) {
    let array = [];
    for (var i in object) {
      array.push({
        label: object[i].descripcionFinalPunto,
        value: object[i].kNroPunto,
      });
    }

    return array;
  }
  function selectListTime(top) {
    let array = [];
    for (var i = 0; i <= top; i++) {
      const seconds = i < 10 ? "0" + i : i;
      array.push({
        label: seconds,
        value: seconds,
      });
    }

    return array;
  }

  const horaValida = REGEX_HORA.test(horaValue);
  const puntosControlOptions = selectList(puntosControl);

  return (
    <div style={{ padding: "40px", minWidth: "500px" }}>
      <Typography gutterBottom variant={"h6"} component="h5">
        Hora Original:
      </Typography>
      <Typography gutterBottom variant={"h5"} component="h2">
        {registry.fechaHora}
        {eventDetail.fechaHora}
      </Typography>

      <TextField
        select
        label="Punto de Control"
        value={kPuntoControlValue}
        onChange={(e) => setKPuntoControlValue(e.target.value)}
        variant="standard"
        size="small"
        fullWidth
        style={{ marginTop: 18 }}
      >
        {puntosControlOptions.map((opcion) => (
          <MenuItem key={opcion.value} value={opcion.value}>
            {opcion.label}
          </MenuItem>
        ))}
      </TextField>

      <div style={{ marginTop: 10 }}>
        <FormControlLabel
          control={
            <Switch
              checked={visibleOBorradoValue}
              onChange={(e) => setVisibleOBorradoValue(e.target.checked)}
              color="primary"
            />
          }
          label="Visible o Borrado"
        />
      </div>

      <TextField
        label="Fecha"
        type="date"
        value={fechaValue}
        onChange={(e) => handleFechaChange(e.target.value)}
        variant="standard"
        size="small"
        fullWidth
        style={{ marginTop: 18 }}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="Hora, minutos, segundos y milisegundos (00:00:00.000)"
        value={horaValue}
        onChange={(e) => handleHoraChange(e.target.value)}
        placeholder="00:00:00.000"
        variant="standard"
        size="small"
        fullWidth
        style={{ marginTop: 18 }}
        error={!horaValida}
        helperText={!horaValida ? "Revisa el formato (00:00:00.000)" : ""}
      />
      {horaSalidaSerie && (
        <Typography variant="body2" style={{ marginTop: 6 }}>
          Hora de salida de la serie:{" "}
          <strong>
            {formatearFechaHora(horaSalidaSerie).fecha}{" "}
            {formatearFechaHora(horaSalidaSerie).hora}
          </strong>
        </Typography>
      )}
      <TextField
        label="o tiempo transcurrido desde la salida (hh:mm:ss)"
        value={tiempoTranscurrido}
        onChange={(e) => handleTiempoChange(e.target.value)}
        placeholder="00:30:00"
        variant="standard"
        size="small"
        fullWidth
        style={{ marginTop: 18 }}
        disabled={!horaSalidaSerie}
        helperText={
          horaSalidaSerie
            ? "Cambiar este campo ajusta la fecha/hora de arriba, y cambiar la fecha/hora ajusta este — usa el que te resulte más cómodo"
            : "No se encontró una hora de salida configurada para la serie de este atleta — no se puede calcular, anota la fecha y hora directamente"
        }
      />

      <Button
        onClick={onSubmit}
        variant="contained"
        color="primary"
        style={{ marginTop: 20, width: "100%" }}
      >
        Guardar Registro
      </Button>
    </div>
  );
}
